/**
 * Build-time feature flags backed by Vite env vars.
 *
 * Spec: docs/architecture/feature-flags.md.
 */

import { useSyncExternalStore } from "react";

export const FLAGS = {
  AUTH_ENABLED: "VITE_FLAG_AUTH_ENABLED",
  SUPABASE_ENABLED: "VITE_FLAG_SUPABASE_ENABLED",
  /**
   * Story Onboarding-1 — when ON, users with completeness_pct < 100 are
   * redirected to /onboarding. Default OFF until Story Onboarding-5 lands
   * the persona step + flips the flag in production.
   */
  ONBOARDING_GATE_ENABLED: "VITE_FLAG_ONBOARDING_GATE_ENABLED",
} as const;

export type FlagName = keyof typeof FLAGS;

const testOverrides = new Map<FlagName, boolean>();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notify(): void {
  listeners.forEach((l) => {
    l();
  });
}

function readEnvFlag(envVar: string): boolean {
  const raw = (import.meta.env as Record<string, unknown>)[envVar];
  if (typeof raw !== "string") return false;
  const v = raw.trim().toLowerCase();
  return v === "true" || v === "1";
}

export function isFeatureEnabled(name: FlagName): boolean {
  const override = testOverrides.get(name);
  if (override !== undefined) return override;
  return readEnvFlag(FLAGS[name]);
}

/** Alias — shorter read site. */
export const isEnabled = isFeatureEnabled;

export function useFeatureFlag(name: FlagName): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isFeatureEnabled(name),
    () => isFeatureEnabled(name),
  );
}

/**
 * Test-only override mechanism. `src/test/setup.ts` must reset overrides
 * in `afterEach`. Production code MUST NOT import `__testing__`.
 */
export const __testing__ = {
  setFlag(name: FlagName, value: boolean): void {
    testOverrides.set(name, value);
    notify();
  },
  clearFlag(name: FlagName): void {
    testOverrides.delete(name);
    notify();
  },
  reset(): void {
    testOverrides.clear();
    notify();
  },
};
